import { DqLoader, DqTable } from "@decentriq/components";
import { type MatchingColumnFormat } from "@decentriq/graphql/dist/types";
import { Box, Tooltip, Typography } from "@mui/joy";
import { format } from "date-fns";
import { type MRT_ColumnDef } from "material-react-table";
import { memo, useCallback, useMemo } from "react";
import { useDataLabsContext } from "features/dataLabs";
import { type DataLab } from "features/dataLabs/models";
import { useDebouncedSearch } from "hooks";
import { matchingIdTypeAndHashingAlgorithmPresentation } from "models";
import { type PublisherDataNodeHookResult } from "../PublisherDataNodeActions/usePublisherDataNodeActions";
import DataLabConnectionTableRowActions from "./DataLabConnectionTableRowActions";
import DataLabConnectionTableToolbar from "./DataLabConnectionTableToolbar";

interface DataLabConnectionTableProps extends PublisherDataNodeHookResult {
  onCancel: () => void;
}

const DataLabConnectionTable = memo<DataLabConnectionTableProps>(
  ({ onCancel, connectDataLab, connecting, dataLabId }) => {
    const {
      dataLabs: { data: dataLabsData, loading },
    } = useDataLabsContext();
    const { debouncedSearch, onSearchChange, searchValue } =
      useDebouncedSearch();
    const filteredData = useMemo<DataLab[]>(
      () =>
        dataLabsData?.filter(({ name }) =>
          name.toLowerCase().includes(debouncedSearch.toLowerCase())
        ) ?? [],
      [dataLabsData, debouncedSearch]
    );
    const handleConnectDataLab = useCallback(
      async (dataLab: DataLab) => {
        await connectDataLab(dataLab);
        onCancel();
      },
      [connectDataLab, onCancel]
    );
    const dateFormat = "dd.MM.yyy, HH:mm";
    const dataLabsColumnDef: MRT_ColumnDef<DataLab>[] = useMemo(
      () => [
        {
          Cell: ({ cell }) => {
            const name = cell.getValue<string>();
            return (
              <Tooltip title={name}>
                <Typography level="body-md" noWrap={true}>
                  {name}
                </Typography>
              </Tooltip>
            );
          },
          accessorKey: "name",
          header: "Name",
          id: "name",
          minSize: 50,
        },
        {
          Cell: ({ cell }) => {
            const createdAt = cell.getValue<string>();
            return createdAt ? format(new Date(createdAt), dateFormat) : "—";
          },
          accessorKey: "updatedAt",
          header: "Last modified",
          id: "updatedAt",
          size: 25,
        },
        {
          Cell: ({ cell }) => {
            const matchingIdFormat = cell.getValue<MatchingColumnFormat>();
            const matchingIdHashingAlgorithm =
              cell.row.original.matchingIdHashingAlgorithm;
            return matchingIdTypeAndHashingAlgorithmPresentation({
              matchingIdFormat,
              matchingIdHashingAlgorithm,
            });
          },
          accessorKey: "matchingIdFormat",
          header: "Matching ID type",
          id: "matchingIdFormat",
          size: 25,
        },
      ],
      []
    );
    if (loading) {
      return (
        <Box
          alignItems="center"
          display="flex"
          justifyContent="center"
          minHeight="5rem"
          width="100%"
        >
          <DqLoader />
        </Box>
      );
    }
    return (
      <DqTable
        columns={dataLabsColumnDef}
        data={filteredData}
        displayColumnDefOptions={{
          "mrt-row-actions": {
            minSize: 200,
          },
        }}
        enableColumnResizing={true}
        enableGlobalFilter={false}
        enableRowActions={true}
        enableSorting={true}
        enableTopToolbar={true}
        initialState={{
          sorting: [{ desc: true, id: "updatedAt" }],
        }}
        localization={{
          actions: "",
          noRecordsToDisplay: "No datalabs found",
        }}
        muiTablePaperProps={{
          sx: {
            display: "flex",
            flex: 1,
            flexDirection: "column",
            overflow: "hidden",
            width: "100%",
          },
        }}
        renderRowActions={({ row }) => (
          <DataLabConnectionTableRowActions
            connectDataLab={handleConnectDataLab}
            dataLab={row.original}
            dataLabId={dataLabId!}
            isConnecting={connecting}
          />
        )}
        renderTopToolbarCustomActions={() => {
          return (
            <DataLabConnectionTableToolbar
              onSearchChange={onSearchChange}
              searchValue={searchValue}
            />
          );
        }}
      />
    );
  }
);

DataLabConnectionTable.displayName = "DataLabConnectionTable";

export default DataLabConnectionTable;
